import { JSX, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { createPortal } from 'react-dom';

import { DrawerBox } from '@/common/components/Drawer/Drawer.styled';
import { DrawerContext } from '@/common/components/Drawer/DrawerContext';
import CriticalIcon from '@/common/icons/CriticalIcons/CriticalIcon';
import { IconName } from '@/common/icons/CriticalIcons/CriticalIcon.enum';

import styles from './drawer.module.scss';

export enum DrawerAnchorType {
  RIGHT = 'right',
  LEFT = 'left',
}

interface DrawerProps {
  children: ReactNode;
  isOpen: boolean;
  handleClose: () => void;
  anchor?: DrawerAnchorType;
  icon?: ReactNode;
  title?: string;
  customHeader?: ReactNode;
  testid?: string;
  boxClassName?: string;
  viewPortHeight?: number;
  drawerHeaderWithButtons?: boolean;
}

const DESKTOP_WIDTH = 440;
const ANIMATION_TIME = 0.4;
const SCROLL_OFFSET = 0;

export const Drawer = ({
  children,
  isOpen,
  handleClose,
  anchor = DrawerAnchorType.RIGHT,
  icon,
  title,
  customHeader,
  testid = 'drawer',
  viewPortHeight = 80,
  drawerHeaderWithButtons = false,
}: DrawerProps): JSX.Element | null => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const ref = contentRef.current;

  const isAnchorLeft = DrawerAnchorType.LEFT;

  const { scrollBarWidth, handleBackdropClose, drawerPortal } = useContext(DrawerContext);
  const [isScrolled, setIsScrolled] = useState((ref?.scrollTop ?? 0) > SCROLL_OFFSET);

  useEffect(() => {
    const callbackFn = () => {
      const scrollPosition = ref?.scrollTop || 0;

      setIsScrolled(scrollPosition > SCROLL_OFFSET);
    };
    document.addEventListener('keydown', handleEscape);

    if (ref) ref.addEventListener('scroll', callbackFn);

    return (): void => {
      document.removeEventListener('keydown', handleEscape);
      if (ref) ref.removeEventListener('scroll', callbackFn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Esc') handleDrawerClose();
  };

  const handleDrawerClose = () => {
    if (handleBackdropClose) handleBackdropClose();
    handleClose();
    setTimeout(() => {
      if (ref) ref.scrollTop = 0;
    }, ANIMATION_TIME * 1000);
  };

  return drawerPortal?.current
    ? createPortal(
        <>
          <DrawerBox
            anchor={anchor}
            animationTime={ANIMATION_TIME}
            data-testid={testid}
            drawerWidth={DESKTOP_WIDTH}
            isOpen={isOpen}
            ref={contentRef}
            scrollBarWidth={scrollBarWidth}
            viewPortHeight={viewPortHeight}
          >
            <div
              className={clsx(styles.drawerHeader, {
                [styles.drawerHeaderScrolled]: isScrolled,
                [styles.drawerAnchorLeft]: isAnchorLeft,
                [styles.drawerHeaderWithButtons]: drawerHeaderWithButtons,
              })}
            >
              {customHeader ?? (
                <div className={styles.drawerTitle}>
                  {icon && <div className={styles.drawerTitleIcon}>{icon}</div>}
                  <h3 className={styles.drawerTitleText}>{title}</h3>
                </div>
              )}
              <CriticalIcon
                className={styles.closeIcon}
                data-testid="drawer-close-button"
                name={IconName.CLOSE}
                onClick={handleDrawerClose}
                strokeWidth={1.5}
                width="24"
              />
            </div>

            <div
              className={clsx(styles.drawerContent, {
                [styles.drawerAnchorLeft]: isAnchorLeft,
              })}
            >
              {children}
            </div>
          </DrawerBox>
          <div
            className={clsx(styles.drawerBackdrop, {
              [styles.drawerBackdropOpened]: isOpen,
            })}
            onClick={handleDrawerClose}
          />
        </>,
        drawerPortal.current,
      )
    : null;
};
